import VideoThumbnailField from '../../components/VideoThumbnailField';
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Autocomplete,
  Box,
  Typography,
} from '@mui/material';
import ChannelBadge from '../../components/ChannelBadge';
import ChannelFootageNotice from '../../components/ChannelFootageNotice';
import ChannelYouTubeEligibilityNotice from '../../components/ChannelYouTubeEligibilityNotice';

export default function CreateVideoDialog({
  open,
  thumbnail,
  setThumbnail,
  thumbnailBusy,
  setThumbnailBusy,
  onClose,
  creating,
  createError,
  onDismissError,
  createTitle,
  setCreateTitle,
  createVideoContext,
  setCreateVideoContext,
  selectedVoiceOverConfig,
  setSelectedVoiceOverConfig,
  voiceOverConfigs,
  voiceOverConfigsLoading,
  loadVoiceOverConfigs,
  selectedQrCode,
  setSelectedQrCode,
  qrCodes,
  qrCodesLoading,
  loadQrCodes,
  selectedChannel,
  setSelectedChannel,
  channels,
  channelsLoading,
  loadChannels,
  handleCreate,
  onOpenAddChannel,
}) {
  return (
    <Dialog open={open} onClose={creating || thumbnailBusy ? undefined : onClose} maxWidth='sm' fullWidth>
      <DialogTitle>New Video</DialogTitle>
      <DialogContent>
        {createError && (
          <Alert
            severity='error'
            sx={{ mb: 2 }}
            onClose={() => (typeof onDismissError === 'function' ? onDismissError() : null)}
          >
            {createError}
          </Alert>
        )}
        <TextField
          margin='dense'
          fullWidth
          label='Title'
          value={createTitle}
          onChange={(e) => setCreateTitle(e.target.value)}
          placeholder='Video title'
          sx={{ mb: 2 }}
        />
        {open && (
          <VideoThumbnailField
            kind='sleep'
            title={createTitle}
            value={thumbnail}
            onChange={setThumbnail}
            disabled={creating || thumbnailBusy}
            onBusyChange={setThumbnailBusy}
          />
        )}
        <TextField
          fullWidth
          multiline
          rows={2}
          label='Script context'
          value={createVideoContext}
          onChange={(e) => setCreateVideoContext(e.target.value)}
          placeholder='Optional context for script generation'
          sx={{ mb: 2 }}
        />
        <Autocomplete
          fullWidth
          options={qrCodes}
          getOptionLabel={(opt) => (opt ? opt.name : '')}
          isOptionEqualToValue={(a, b) => a && b && a.id === b.id}
          value={selectedQrCode}
          onChange={(_, val) => setSelectedQrCode(val)}
          loading={qrCodesLoading}
          filterOptions={(x) => x}
          onOpen={() => loadQrCodes()}
          renderInput={(params) => (
            <TextField {...params} label='QR Code' placeholder='None' />
          )}
          sx={{ mb: 2 }}
        />
        <Autocomplete
          fullWidth
          options={voiceOverConfigs}
          getOptionLabel={(opt) => (opt ? opt.name : '')}
          value={selectedVoiceOverConfig}
          onChange={(_, val) => setSelectedVoiceOverConfig(val)}
          loading={voiceOverConfigsLoading}
          filterOptions={(x) => x}
          onOpen={() => loadVoiceOverConfigs()}
          renderInput={(params) => (
            <TextField {...params} label='Voice Over' required={!selectedChannel?.voiceOverConfigId}
              helperText={selectedChannel?.voiceOverConfigId
                ? `Leave empty to use the channel default: ${voiceOverConfigs.find(config => config.id === selectedChannel.voiceOverConfigId)?.name || 'saved voice'}`
                : 'Choose a voice or select a channel with a default voice.'} />
          )}
          sx={{ mb: 2 }}
        />
        {voiceOverConfigs.length === 0 && !voiceOverConfigsLoading && (
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            No voice over configs. Create one via the Voice Over button first.
          </Typography>
        )}
        <Autocomplete
          fullWidth
          options={channels}
          getOptionLabel={(opt) =>
            (opt &&
              (opt.profileNumber != null
                ? `${opt.name} (#${opt.profileNumber})`
                : opt.name)) ||
            ''
          }
          value={selectedChannel}
          onChange={(_, val) => setSelectedChannel(val)}
          loading={channelsLoading}
          filterOptions={(x) => x}
          onOpen={() => loadChannels('')}
          onInputChange={(_, value) => loadChannels(value || '')}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <ChannelBadge
                channel={{
                  ...option,
                  name:
                    option.profileNumber != null
                      ? `${option.name} (#${option.profileNumber})`
                      : option.name,
                }}
              />
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} label='Channel' required />
          )}
        />
        <ChannelFootageNotice channel={selectedChannel} kind="sleep" />
        {open && <ChannelYouTubeEligibilityNotice channel={selectedChannel} kind="sleep" />}
        <Box sx={{ mt: 1 }}>
          <Button size='small' onClick={onOpenAddChannel}>
            Add new channel
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={creating || thumbnailBusy}>
          Cancel
        </Button>
        <Button variant='contained' onClick={handleCreate} disabled={creating || thumbnailBusy}>
          {creating ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
